import { Button, SaveButton, CloseButton, IconButton } from '@ui/shared/Button';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import styled from 'styled-components';
import {
  closeInnerPopup,
  openInnerPopup,
} from '../../../../data/providers/popupsProvider';
import {
  ICoach,
  permissionsProvider,
} from '../../../../data/providers/usersProvider';
import { IPermission, IUser } from '../../../../data/types';
import {
  disableUser,
  updatePermissions,
} from '../../../../data/writers/userManagement';
import { Row } from '../../../layout/Row';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import {
  InnerPopupCard,
  PopupCardContent,
} from '../../../shared/popups/PopupCard';
import { AddPermissionForm } from './AddPermissionForm';
import { PermissionsList } from './PermissionsList';

interface IProps extends RouteComponentProps {
  requestingUser: IUser;
  user: ICoach;
}

/**
 * @description button to open the edit user popup
 */
export const EditUser = withRouter((props: IProps): React.ReactElement => {
  const openDialog = (): void =>
    openInnerPopup(
      <Popup
        onClose={closeInnerPopup(props.history)}
        user={props.user}
        requestingUser={props.requestingUser}
      />,
      props.history,
      'add'
    );

  return <IconButton onClick={openDialog} icon='edit' title='Edit User' />;
});

interface IEditUserPopupProps {
  onClose: () => void;
  user: ICoach;
  requestingUser: IUser;
}

/**
 * @description edit user popup
 * @param props
 */
const Popup = (props: IEditUserPopupProps): React.ReactElement => {
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [coach, setCoach] = React.useState<ICoach>();
  const [permissions, setPermissions] = React.useState<IPermission[]>([]);

  const [error, setError] = React.useState('');

  React.useEffect((): (() => void) => {
    const subscriber = permissionsProvider(props.user).subscribe(
      (mCoach: ICoach): void => {
        setCoach(mCoach);
        setPermissions(mCoach.permissions);
        setLoading(false);
      }
    );
    return (): void => subscriber.unsubscribe();
  }, []);

  /**
   * @description saves the user
   */
  const saveUser = async (): Promise<void> => {
    setSaving(true);
    await updatePermissions(coach.id, coach.permissions, permissions);
    setSaving(false);
    props.onClose();
  };

  /**
   * @description disables user account
   */
  const disableAccount = (): void => {
    setSaving(true);
    disableUser(props.user).then((): void => props.onClose());
  };

  /**
   * @description handles change to permissions
   * @param mPermissions
   */
  const permissionsChangeHandler = (mPermissions: IPermission[]): void =>
    setPermissions(mPermissions);

  return (
    <InnerPopupCard onClose={props.onClose} size='medium' noClickOutside>
      <PopupCardContent style={{ width: '100%' }}>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h5>Edit {props.user.name}</h5>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {loading ? (
            <LinearLoading />
          ) : saving ? (
            <LinearLoading />
          ) : (
            <Row shouldCollapse>
              <LeftSide>
                <PermissionsList
                  onChange={permissionsChangeHandler}
                  permissions={permissions}
                />
              </LeftSide>
              <RightSide>
                <AddPermissionForm
                  onChange={permissionsChangeHandler}
                  requestingUser={props.requestingUser}
                  existingPermissions={permissions}
                />
              </RightSide>
            </Row>
          )}
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft>
            <Button
              disabled={saving}
              onClick={disableAccount}
              title='Delete User'
              icon='delete'
             />
          </CardFooterLeft>
          <CardFooterRight>
            <SaveButton disabled={saving} onClick={saveUser} />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </InnerPopupCard>
  );
};

const LeftSide = styled.div`
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const RightSide = styled.div`
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  width: 100%;
`;
